import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Form, Input, Select, Spin } from 'antd';
import { API } from '@/services/api.service';
import { errorText } from './format';

const ACCOUNT_FIELDS = ['expense', 'taxLiability', 'netLiability', 'interestExpense'];

/**
 * Finished calculations: who at HLB gets the mail with the workbook and the
 * AB journal when a calculation is marked done, and the accounts and folder
 * the rent journal books to (PCCC's by default, as Milica books them).
 */
export default function CalculationSettingsCard({ company }) {
  const { t } = useTranslation('taxReturns');
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const key = ['tax-calculation-settings', company];

  const { data: s, error } = useQuery({
    queryKey: key,
    queryFn: () => API.taxReturns.getCalculationSettings(company),
    enabled: Boolean(company),
  });

  useEffect(() => {
    if (s) form.setFieldsValue(s);
  }, [s, form]);

  const save = useMutation({
    mutationFn: (data) => API.taxReturns.updateCalculationSettings(company, data),
    onSuccess: (data) => queryClient.setQueryData(key, data),
  });

  return (
    <Card size="small" title={t('calcSettings.title')}>
      <p className="text-sm opacity-70" style={{ marginBottom: 12 }}>
        {t('calcSettings.intro')}
      </p>
      {error && <Alert type="error" showIcon className="!static" message={errorText(t, error)} />}
      {!s && !error && <Spin />}
      {s && (
        <Form form={form} layout="vertical" onFinish={(v) => save.mutate({ ...v, notifyEmails: v.notifyEmails || [] })}>
          <Form.Item name="notifyEmails" label={t('calcSettings.notifyEmails')} tooltip={t('calcSettings.notifyEmailsHelp')}>
            <Select mode="tags" tokenSeparators={[',', ';', ' ']} placeholder="name@hlb.rs" open={false} />
          </Form.Item>
          <div className="grid gap-x-4 sm:grid-cols-2 md:grid-cols-5">
            {ACCOUNT_FIELDS.map((f) => (
              <Form.Item key={f} name={f} label={t(`calcSettings.${f}`)} rules={[{ pattern: /^\d{3,10}$/, message: t('errors.ab_invalid_account') }]}>
                <Input />
              </Form.Item>
            ))}
            <Form.Item
              name="folder"
              label={t('calcSettings.folder')}
              tooltip={t('calcSettings.folderHelp')}
              rules={[{ pattern: /^[A-Za-z0-9]{1,4}$/, message: t('errors.ab_invalid_folder') }]}
            >
              <Input />
            </Form.Item>
          </div>
          <p className="text-xs opacity-60">{t('calcSettings.journalHelp')}</p>
          {save.isError && <Alert type="error" showIcon className="!static" message={errorText(t, save.error)} style={{ marginTop: 12 }} />}
          <div className="mt-3 flex justify-end">
            <Button shape="round" type="primary" htmlType="submit" loading={save.isPending}>
              {t('drive.save')}
            </Button>
          </div>
        </Form>
      )}
    </Card>
  );
}
